import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import Guid from "_shared/guids/guid";
import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import UpdateEntityAgreementAPIRequest from "legal-entities/agreements/_api/commands/update-entity-agreement-api-command";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import Session from "users/__session/session";
import { AuthResponse } from "users/_api/auth-email-api-service";
import { AuthInfo } from "users/auth/info/auth-info";
import AuthInfoSchema from "users/auth/info/auth-info-schema";

export default class EntityAgreementAPIService {
  public constructor(session: Readonly<Session>) {
    if (!session.authToken) throw new Error("Cannot create EntityAgreementAPIService without session.");
    this._headers.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  public async getEntityAgreementById(id: Guid, abortController?: AbortController): Promise<EntityAgreement> {
    const url = new URL(`/legal-entities/entity-agreements/${id.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this._headers,
        signal: abortController?.signal,
      });
      return EntityAgreement.fromJSON(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error?.response?.status === HttpStatusCode.NotFound)
        throw new EntityAgreementNotFoundError(id);
      if (error instanceof CanceledError) throw error;
      throw new EntityAgreementAPIServiceError("getEntityAgreementById", error);
    }
  }

  public async getEntityAgreementsByIds(ids: Guid[], abortController?: AbortController): Promise<EntityAgreement[]> {
    let url = new URL("/legal-entities/entity-agreements/get-by-ids", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        ids.map((id) => id.value),
        {
          headers: this._headers,
          signal: abortController?.signal,
        },
      );
      return EntityAgreement.fromJSONArray(response.data) ?? [];
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest)
        throw new InvalidEntityAgreementRequestError(error.message);
      if (error instanceof CanceledError) throw error;
      throw new EntityAgreementAPIServiceError("getEntityAgreementsByIds", error);
    }
  }

  public async getEntityAgreementTerms(agreement: EntityAgreement, abortController: AbortController): Promise<Blob> {
    const url = new URL(`/legal-entities/agreements/${agreement.id.value}/terms`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this._headers,
        responseType: "blob",
        signal: abortController?.signal,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error?.response?.status === HttpStatusCode.Forbidden)
        throw new UnauthorizedEntityAgreementAccessError(agreement.id);
      if (axios.isAxiosError(error) && error?.response?.status === HttpStatusCode.NotFound)
        throw new EntityAgreementNotFoundError(agreement.id);
      if (error instanceof CanceledError) throw error;
      throw new EntityAgreementAPIServiceError("getEntityAgreementTerms", error, agreement);
    }
  }

  public async updateEntityAgreement(
    oldAgreement: EntityAgreement,
    updatedAgreement: EntityAgreement,
  ): Promise<EntityAgreement> {
    const url = new URL(`/legal-entities/entity-agreements/${oldAgreement.id.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.patch(
        url.toString(),
        new UpdateEntityAgreementAPIRequest(oldAgreement, updatedAgreement).payload,
        {
          headers: this._headers.concat({ "Content-Type": "application/json-patch+json" }),
        },
      );
      return EntityAgreement.fromJSON(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new EntityAgreementNotFoundError(oldAgreement.id);
      throw new EntityAgreementAPIServiceError("updateEntityAgreement", error, oldAgreement);
    }
  }

  public async acceptEntityAgreement(entityAgreement: EntityAgreement): Promise<AuthResponse> {
    const url = new URL(
      `/legal-entities/entity-agreements/${entityAgreement.id.value}/accept`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      const apiResponse = await axios.post(
        url.toString(),
        {},
        {
          headers: this._headers.concat({ "Content-Type": "application/json-patch+json" }),
        },
      );
      return new AuthInfo(AuthInfoSchema.deserialize(apiResponse.data)).toAuthResponse();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new EntityAgreementNotFoundError(entityAgreement.id);
      throw new EntityAgreementAPIServiceError("acceptEntityAgreement", error, entityAgreement);
    }
  }

  private readonly _headers: AxiosHeaders = new AxiosHeaders();
}

export class UnauthorizedEntityAgreementAccessError extends Error {
  constructor(id: Guid) {
    super(`You are not permitted to view this agreement: ${id}`);
  }
}

export class EntityAgreementNotFoundError extends Error {
  public constructor(id: Guid) {
    super(`Unable to find agreement by id: ${id}`);
  }
}

export class InvalidEntityAgreementError extends Error {}

export class InvalidEntityAgreementRequestError extends Error {}

export class EntityAgreementAPIServiceError extends Error {
  public methodName: string;
  public error: unknown;
  public agreement?: EntityAgreement;
  public constructor(methodName: string, error: unknown, agreement?: EntityAgreement) {
    super(
      `Error in EntityAgreementAPIService.${methodName}: ${error instanceof Error ? error.message : String(error)}`,
    );
    this.methodName = methodName;
    this.error = error;
    this.agreement = agreement;
  }
}
